import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { isTransientApiError } from './transient';
import { isExpectedClientError } from './expectedClient';

const DEDUPE_TTL_MS = 1000;
const lastReportedAt = new Map<string, number>();

type Level = 'warning' | 'error' | 'info';

interface Options {
  userMessage?: string;
  extra?: Record<string, unknown>;
  level?: Level;
  /**
   * Opt back in to Sentry capture for an error the guard would otherwise treat
   * as an expected client (4xx) error. Pass `expected: false` when a specific
   * 4xx is a known *real* bug (e.g. the #3738 FormData 422 contract mismatch)
   * that must still reach GlitchTip. Has no effect on errors that are not
   * expected-client (5xx, network, plain errors always report).
   */
  expected?: boolean;
}

function fingerprint(where: string, err: unknown): string {
  if (err instanceof Error) {
    const firstWord = (err.message ?? '').split(/\s+/)[0] ?? '';
    return `${where}|${err.name}|${firstWord}`;
  }

  return `${where}|${typeof err}|${String(err).slice(0, 20)}`;
}

/**
 * Report a caught error to Sentry (and optionally toast the user).
 *
 * ADR-0007 Layer 5: every catch block must report. `console.warn`/`console.error`
 * in a catch is prohibited because it never reaches GlitchTip, which means the
 * Sentry -> GlitchTip -> auto-mitigation pipeline cannot act on it.
 *
 * Two call-site shapes:
 *
 *   // USER_ACTION_FAILURE — toast + Sentry:
 *   reportError(err, 'StaffingBoard.deleteAssignment', {
 *     userMessage: 'Failed to delete assignment',
 *   });
 *
 *   // BEST_EFFORT_POLISH — Sentry only:
 *   reportError(err, 'StaffingBoard.scrollRestore');
 *
 * Anti-patterns (all flagged by Hookify + ESLint):
 *
 *   // LOG_ONLY — does not reach Sentry:
 *   catch (err) { console.error(err); }
 *
 *   // TOAST_ONLY — user told, Sentry silent:
 *   catch (err) { toast.error('Failed'); }
 *
 *   // EMPTY_CATCH — prohibited:
 *   catch {}; .catch(() => {}); .catch(() => false);
 *
 *   // DOUBLE_CAST — unnecessary (reportError takes unknown):
 *   catch (err) { reportError(err as unknown as Error, 'Module.operation'); }
 *
 * Dedupes repeated Sentry captures for the same `(where + error-name +
 * first-message-word)` fingerprint within 1s. Toasts still fire on every call
 * so users see each failed action.
 *
 * @param err - The caught error. Any shape; Sentry will normalize it.
 * @param where - Dotted path identifying the catch site. E.g. "Module.op".
 * @param options.userMessage - Toast copy. Omit to suppress the toast.
 * @param options.extra - Structured context for Sentry.
 * @param options.level - 'warning' | 'error' (default) | 'info'.
 * @param options.expected - Pass `false` to force Sentry capture for a 4xx the
 *   `isExpectedClientError` guard would otherwise skip (a known-real 4xx bug).
 */
export function reportError(err: unknown, where: string, options?: Options): void {
  const level: Level = options?.level ?? 'error';
  const key = fingerprint(where, err);
  const now = Date.now();
  const last = lastReportedAt.get(key) ?? 0;

  // Two classes of error skip Sentry capture, for the same reason — they are
  // routine app traffic, not defects, and capturing them only floods GlitchTip:
  //   1. Transient errors (timeout, network failure, 502/503/504) — momentary
  //      hiccups; the API interceptor already toasts these (GT-SCALE-3366).
  //   2. Expected client (4xx) errors — expired session, validation, permission,
  //      not-found probes (see ./expectedClient.ts; the 26× 401 noise that
  //      motivated it). `options.expected === false` forces a known-real 4xx
  //      (e.g. #3738) to report.
  // Toasts/logging/UX below are unchanged: the interceptor still toasts these,
  // and the caller's catch still runs.
  const skipAsExpectedClient = options?.expected !== false && isExpectedClientError(err);

  if (!isTransientApiError(err) && !skipAsExpectedClient && now - last >= DEDUPE_TTL_MS) {
    lastReportedAt.set(key, now);
    Sentry.captureException(err, {
      level,
      extra: { where, ...(options?.extra ?? {}) },
    });
  }

  if (options?.userMessage) {
    toast.error(options.userMessage);
  }
}

/**
 * Report and flush Sentry before returning.
 * Callers must `await` this when the surrounding context is about to unload.
 */
export async function reportErrorAndFlush(
  err: unknown,
  where: string,
  options?: Options,
): Promise<void> {
  reportError(err, where, options);
  await Sentry.flush(2000);
}
